/**
 * "Report + block" combined flow.
 *
 * Threat model
 * ============
 *
 * Signal's standard abuse UX ships a "Report and block" affordance: a
 * single action that fires both a SpamReport (server-bound, deniable to
 * the body) AND a BlockSyncPayload (cross-device sync of the block
 * decision). Splitting these into two independent calls would invite skew
 * bugs: e.g. the report ships but the block doesn't, so the user keeps
 * receiving messages from the abuser. The coordinator guarantees both side
 * effects are produced atomically from the caller's perspective — either
 * the function returns both or it throws, leaving neither stored.
 *
 * Hardening
 * ---------
 *
 *   - Self-report is rejected (you cannot report yourself) — caught
 *     before any state mutation.
 *   - Local cooldown is consulted BEFORE any state mutation. If the
 *     cooldown rejects, no SpamReport is signed, no block is installed,
 *     and no payload is returned.
 *   - The block reason is hard-coded to `"spamFlag"` so a UI bug cannot
 *     mis-label the block as `"userInitiated"`.
 *   - The SpamReport's `messageIdHash` is computed from the offending
 *     messageId LOCALLY via `SpamReportCodec.computeMessageIdHash`; the
 *     raw messageId never leaves this function.
 *   - The cooldown ledger is advanced ONLY after signing + block
 *     installation have both succeeded. A transient signing failure does
 *     NOT eat into the user's cooldown budget.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/SpamReportAndBlockCoordinator.swift`.
 */

import {
  SpamReportCodec,
  SpamReportError,
  SPAM_REPORT_SCHEMA_VERSION,
} from "./SpamReport";
import type { SpamReportCategory, SpamReport } from "./SpamReport";
import { SpamReportStore } from "./SpamReportStore";
import { BlockListStore } from "../block-list/BlockListStore";
import type { BlockSyncPayload } from "../block-list/BlockListEntry";

// ---------------------------------------------------------------------------
// SpamReportAndBlockOutcome
// ---------------------------------------------------------------------------

/**
 * Successful outcome of a combined report + block.
 * Mirrors `SpamReportAndBlockOutcome` in iOS.
 */
export interface SpamReportAndBlockOutcome {
  /**
   * The encoded SpamReport (`kaether:spam:report:v1:<base64url>`).
   * Caller ships this to the server's /abuse/report endpoint —
   * currently unwired; see `kaether_for_web/docs/spam-report.md`.
   */
  readonly reportShippedPayload: string;
  /**
   * The cross-device block sync payload. Caller ships this through the
   * existing pairwise E2EE channel to each of the user's other linked
   * devices, exactly like any other control message.
   */
  readonly blockPayload: BlockSyncPayload;
}

// ---------------------------------------------------------------------------
// SpamReportAndBlockError
// ---------------------------------------------------------------------------

export type SpamReportAndBlockErrorKind =
  | {
      /** Too many reports against this peer in the cooldown window. */
      readonly tag: "cooldownActive";
    }
  | {
      /** reporterUserId == reportedUserId. Cannot report yourself. */
      readonly tag: "reporterEqualsReported";
    }
  | {
      /**
       * The reporter's identity private key was missing or unusable.
       * Surfaced from `SpamReportCodec.sign` failures.
       */
      readonly tag: "reporterMissingIdentityKey";
      readonly detail: string;
    };

export class SpamReportAndBlockError extends Error {
  constructor(public readonly kind: SpamReportAndBlockErrorKind) {
    super(SpamReportAndBlockError.message(kind));
    this.name = "SpamReportAndBlockError";
  }

  private static message(kind: SpamReportAndBlockErrorKind): string {
    switch (kind.tag) {
      case "cooldownActive":
        return "cooldown active: too many reports against this peer in the current window";
      case "reporterEqualsReported":
        return "cannot report yourself (reporterUserId === reportedUserId)";
      case "reporterMissingIdentityKey":
        return `reporter identity key unusable: ${kind.detail}`;
    }
  }
}

// ---------------------------------------------------------------------------
// reportAndBlock options
// ---------------------------------------------------------------------------

export interface ReportAndBlockOptions {
  /**
   * The BlockListStore to persist the block into.
   * Defaults to the shared production instance.
   */
  blockStore?: BlockListStore;
  /**
   * The SpamReportStore enforcing the cooldown.
   * Defaults to the shared production instance.
   */
  reportStore?: SpamReportStore;
  /**
   * Override the current time (Unix seconds). Used in tests to control
   * the cooldown window and `reportedAt`. Defaults to `Date.now()/1000`.
   */
  nowSeconds?: number;
}

// ---------------------------------------------------------------------------
// SpamReportAndBlockCoordinator
// ---------------------------------------------------------------------------

/**
 * Build a signed SpamReport AND install + emit a block in one atomic step.
 *
 * Mirrors `SpamReportAndBlockCoordinator.reportAndBlock` in iOS.
 *
 * @param reporterUserId  The local user's userId.
 * @param reporterIdentityPrivateKey  32-byte Ed25519 seed (the local user's
 *   identity private key).
 * @param reportedUserId  The peer being reported.
 * @param category  Categorical reason (NEVER free text).
 * @param offendingMessageId  messageId of the offending message; hashed
 *   locally before being placed in the wire payload.
 * @param options  Optional overrides for stores and clock.
 *
 * @throws {SpamReportAndBlockError} with tag "reporterEqualsReported" if
 *   reporterUserId === reportedUserId.
 * @throws {SpamReportAndBlockError} with tag "cooldownActive" if the local
 *   cooldown rejects the call.
 * @throws {SpamReportAndBlockError} with tag "reporterMissingIdentityKey"
 *   if signing fails.
 */
export async function reportAndBlock(
  reporterUserId: string,
  reporterIdentityPrivateKey: Uint8Array,
  reportedUserId: string,
  category: SpamReportCategory,
  offendingMessageId: string,
  options: ReportAndBlockOptions = {},
): Promise<SpamReportAndBlockOutcome> {
  const {
    blockStore = new BlockListStore(),
    reportStore = new SpamReportStore(),
    nowSeconds = Math.floor(Date.now() / 1000),
  } = options;

  // --- Pre-flight guards (no state mutation yet) ---

  if (reporterUserId === reportedUserId) {
    throw new SpamReportAndBlockError({ tag: "reporterEqualsReported" });
  }

  const canReport = await reportStore.canReport(reportedUserId, nowSeconds);
  if (!canReport) {
    throw new SpamReportAndBlockError({ tag: "cooldownActive" });
  }

  // --- Build + sign the SpamReport ---

  const messageIdHash = await SpamReportCodec.computeMessageIdHash(
    offendingMessageId,
  );

  const payload = {
    schema: SPAM_REPORT_SCHEMA_VERSION,
    reportedUserId,
    reporterUserId,
    category,
    messageIdHash,
    reportedAt: nowSeconds,
  };

  let signedReport: SpamReport;
  try {
    signedReport = await SpamReportCodec.sign(
      payload,
      reporterIdentityPrivateKey,
    );
  } catch (err) {
    // Either the private key is malformed, or one of the structural
    // preconditions in sign(...) tripped. Both are caller-fault but
    // we surface a single error to the UI.
    const detail =
      err instanceof SpamReportError
        ? err.message
        : err instanceof Error
          ? err.message
          : String(err);
    throw new SpamReportAndBlockError({
      tag: "reporterMissingIdentityKey",
      detail,
    });
  }

  const encoded = SpamReportCodec.encode(signedReport);

  // --- Install the block (reason hard-coded to "spamFlag") ---
  const blockPayload = await blockStore.block(reportedUserId, nowSeconds, "spamFlag");

  // --- Advance the cooldown ledger AFTER signing + blocking ---
  //
  // If signing had thrown above, we'd never reach this line — the ledger
  // is NOT touched on a transient signing failure. This preserves the
  // user's cooldown budget for future attempts.
  await reportStore.recordReport(reportedUserId, nowSeconds);

  return {
    reportShippedPayload: encoded,
    blockPayload,
  };
}
